import React, { useEffect } from "react"

import { Loader2, MessageSquare, Send, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { OVERLAY_SCRIM, PANEL_WIDTH } from "@/components/ui/overlay"
import { Textarea } from "@/components/ui/textarea"
import { formatDateTimeShort } from "@/lib/dateFormats"
import { cn } from "@/lib/utils"

/**
 * Shared frame for the route and stop details panels (the planning route-map rail uses the header
 * too).
 */

/** `fixed`, not `absolute`: must not be clipped by the host's scroll container. */
export const DetailsPanelFrame: React.FC<{
  /** A caller with its own dismissable layer (assignment pickers) closes that first and returns. */
  onDismiss: () => void
  children: React.ReactNode
}> = ({ onDismiss, children }) => {
  /**
   * Hand-rolled rail, so Escape is wired here; skipped while a dialog or popper above owns the key.
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-radix-popper-content-wrapper]'
        )
      ) {
        return
      }
      onDismiss()
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onDismiss])

  return (
    <>
      <div className={cn("fixed inset-0 z-40", OVERLAY_SCRIM)} onClick={onDismiss} />
      {/* No stopPropagation: Radix dismisses poppers on document clicks, so swallowing them
          leaves pickers stuck open. */}
      <div className={cn("fixed inset-y-0 right-0 z-50 shadow-xl", PANEL_WIDTH.default)}>
        <div className="flex h-full min-w-0 flex-col border-l border-palette-jungleGreen200 bg-white">
          {children}
        </div>
      </div>
    </>
  )
}

/** text-lg, not text-xl, so a long name plus status pill fits on one line at 460px. */
export const DetailsPanelHeader: React.FC<{
  leading?: React.ReactNode
  title: string
  /** Keep to two: the row also holds the close button. */
  meta?: React.ReactNode
  subtitle?: React.ReactNode
  closeLabel: string
  onClose: () => void
  actions?: React.ReactNode
}> = ({ leading, title, meta, subtitle, closeLabel, onClose, actions }) => (
  <div className="flex-shrink-0 border-b border-palette-jungleGreen200 px-5 py-4">
    <div className="flex items-center gap-2.5">
      {leading}
      <h2
        className="min-w-0 truncate text-lg font-semibold text-palette-jungleGreen900"
        title={title}
      >
        {title}
      </h2>
      {meta}
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        aria-label={closeLabel}
        className="ml-auto h-7 w-7 flex-shrink-0 text-palette-jungleGreen500 hover:text-palette-jungleGreen900"
      >
        <X size={16} />
      </Button>
    </div>
    {subtitle && <p className="mt-1 truncate text-xs text-palette-jungleGreen500">{subtitle}</p>}
    {actions && <div className="mt-3">{actions}</div>}
  </div>
)

/** Rows, not cards: at rail width a card truncates long DRB names and addresses. */
export const SummaryRow: React.FC<{
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}> = ({ icon, label, children }) => (
  <div className="flex items-center gap-3 px-3 py-2">
    <span className="flex w-[70px] flex-none items-center gap-1.5 text-xs text-palette-jungleGreen500">
      {icon}
      {label}
    </span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
)

export const SummaryCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      "divide-y divide-palette-jungleGreen200 rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000",
      className
    )}
  >
    {children}
  </div>
)

export const PanelSectionHeading: React.FC<{
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
}> = ({ icon, children, className }) => (
  <h3
    className={cn(
      "mb-3 flex items-center gap-2 text-sm font-semibold text-palette-jungleGreen700",
      className
    )}
  >
    {icon}
    {children}
  </h3>
)

export interface PanelComment {
  id: string
  author: string
  content: string
  createdAt: Date | string
}

export const CommentThread: React.FC<{
  title: string
  comments: PanelComment[]
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  isPending?: boolean
}> = ({ title, comments, value, onChange, onSubmit, isPending = false }) => (
  <div>
    <PanelSectionHeading icon={<MessageSquare size={14} />}>{title}</PanelSectionHeading>

    <div className="mb-3 space-y-2">
      {comments.length === 0 ? (
        <div className="text-sm italic text-palette-jungleGreen400">No comments yet</div>
      ) : (
        comments.map((c) => (
          <div
            key={c.id}
            className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3"
          >
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-medium text-palette-jungleGreen900">{c.author}</span>
              <span className="text-xs text-palette-jungleGreen400">
                {formatDateTimeShort(new Date(c.createdAt))}
              </span>
            </div>
            <p className="text-sm text-palette-jungleGreen600">{c.content}</p>
          </div>
        ))
      )}
    </div>

    <div>
      <Textarea
        placeholder="Add a comment..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[60px] resize-none border-palette-jungleGreen200 bg-white text-palette-jungleGreen900 placeholder:text-palette-jungleGreen400"
        disabled={isPending}
      />
      <div className="mt-2 flex items-center justify-end">
        <Button
          size="sm"
          onClick={onSubmit}
          disabled={isPending || !value.trim()}
          className="h-7 gap-1.5 bg-amber-500 text-xs text-white hover:bg-amber-600"
        >
          {isPending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          {isPending ? "Adding…" : "Add comment"}
        </Button>
      </div>
    </div>
  </div>
)
